import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';

import { cancelOperation } from '@/api/operation';

import { Icon } from '@/components/Icon/Icon';
import {
  InfoMenuButton,
  InfoMenuDivider,
} from '@/components/Layout/Drawer/Drawer.styled';
import { InfoDrawer } from '@/components/Layout/Drawer/InfoDrawer';
import { Avatar } from '@/components/User/Avatar';

import { useConfirmation, useDrawerOpenTracker } from '@/context';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { requestSignOut, signOut } from '@/redux/auth/actions';
import { selectAuthState } from '@/redux/auth/selectors';
import { selectRightStartFeature } from '@/redux/configs';
import { hideUserInfo, setUserSelectedLanguage } from '@/redux/ui';

import { Tenant } from '@/types/common';
import { Language } from '@/types/i18n';

import { getFullName } from '@/utils/string';
import { isRemediationCustomer } from '@/utils/user';

export function UserInfoDrawer() {
  const { user } = useAppSelector(selectAuthState);
  const { t, language } = useTranslate();

  const enabledRightStartAccount = useAppSelector(selectRightStartFeature);
  const { showConfirmation } = useConfirmation();
  const dispatch = useAppDispatch();
  const theme = useTheme();

  // Only mounted while isShowingUserInfo is true, so it's always "open" for
  // as long as it's mounted.
  useDrawerOpenTracker(true);

  const onHideUserInfo = () => dispatch(hideUserInfo());
  const onRequestSignOut = () => {
    onHideUserInfo();
    dispatch(requestSignOut());
  };

  const { mutate: cancelApplicationMutate } = useMutation({
    mutationKey: ['cancelOperation'],
    mutationFn: cancelOperation,
    onSuccess: () => {
      dispatch(signOut());
    },
  });

  const onCancelApplication = () => {
    showConfirmation({
      title: t('cancelApplication.confirmTitle'),
      description: t('cancelApplication.confirmDescription'),
      primaryText: t('common.confirm'),
      secondaryText: t('common.cancel'),
      onCancel: () => {},
      onConfirm: () => {
        if (user?.operationId) {
          cancelApplicationMutate({ operationId: user.operationId });
        }
      },
    });
  };

  const handleLanguageChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newLanguage: Language | null) => {
      if (newLanguage && user?.language !== newLanguage) {
        dispatch(setUserSelectedLanguage(newLanguage));
      }
    },
    [dispatch, user?.language],
  );

  return (
    <InfoDrawer
      description={
        <Stack direction="column" gap={1}>
          <Typography variant="h3">{getFullName(user)}</Typography>
          <Typography variant="body2" color={theme.palette.grey[800]}>
            @{user?.username}
          </Typography>
        </Stack>
      }
      drawerImage={
        <Stack justifyContent="center" mb={2.5}>
          <Avatar size={96}>{user?.firstName}</Avatar>
        </Stack>
      }
      content={
        <Stack direction="column" flexGrow={1} gap={1} sx={{ width: '100%' }}>
          <InfoMenuDivider />
          {user?.tenant === Tenant.Suriname && (
            <>
              <Box width="100%" px={1}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Icon
                      name="globe-simple"
                      color={theme => theme.palette.grey[700]}
                    />
                    <Typography color={theme.palette.text.primary}>
                      {t('common.language')}
                    </Typography>
                  </Stack>
                  <ToggleButtonGroup
                    value={language}
                    exclusive
                    onChange={handleLanguageChange}
                    aria-label="language"
                    size="small"
                  >
                    <ToggleButton value={Language.English}>
                      {t('language.en')}
                    </ToggleButton>
                    <ToggleButton value={Language.Dutch}>
                      {t('language.nl')}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              </Box>
              <InfoMenuDivider />
            </>
          )}
          {!isRemediationCustomer(user) && enabledRightStartAccount && (
            <InfoMenuButton onClick={onCancelApplication}>
              <Stack direction="row" gap={1}>
                <Typography color={theme.palette.text.primary}>
                  {t('common.cancelApplication')}
                </Typography>
              </Stack>
            </InfoMenuButton>
          )}

          <InfoMenuButton onClick={onRequestSignOut}>
            <Stack direction="row" gap={1}>
              <Icon name="sign-out" color={theme => theme.palette.grey[700]} />
              <Typography color={theme.palette.text.primary}>
                {t('common.logOut')}
              </Typography>
            </Stack>
          </InfoMenuButton>
        </Stack>
      }
      onClose={onHideUserInfo}
    />
  );
}
